import React, { useEffect, useState, useRef } from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  InputAdornment,
} from "@mui/material";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import { green } from "@mui/material/colors";
import LockIcon from "@mui/icons-material/Lock";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import {
  entity_id,
  orderID,
  Platform,
  payuPayment,
  returnurl,
  generateOrderId,
  ApplicationType,
  locationGeo,
  date,
  country,
} from "../Utils/Const";
import { baseapi } from "../Environmentalurl";
import DashboardHeader from "../Dashboard/DashboardHeader";
import { useSelector } from "react-redux";
import useIPAddress from "../Utils/useIPAddress";
import api from "../Utils/Api";

const Payuccdc = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [cardType, setCardType] = useState("credit");
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");
  const [nameOnCard, setNameOnCard] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false); // ✅ overlay state
  const [user, setUser] = useState({});
  const [cardInfo, setCardInfo] = useState(null);
  const [binFetched, setBinFetched] = useState(false);
  const userData = useSelector((state) => state.auth.user);

  const location = useLocation();
  const navigate = useNavigate();
  const ip = useIPAddress();
  const expiryRef = useRef(null);

  const amount = location.state?.amount || "10.00";

  const CARD_LOGOS = {
    VISA: "/Assets/cards/32px-Visa_Inc._logo.svg.png",
    MAST: "/Assets/cards/MasterCard_Logo.svg.png",
    RUPAY: "/Assets/cards/Rupay-Logo.png",
    AMEX: "/Assets/cards/American_Express_logo_(2018).svg.png",
    DISCOVER: "/Assets/cards/Discover-logo.png",
    DINERS: "/Assets/cards/diners-club.png",
    JCB: "/Assets/cards/JCB_logo.svg.png",
    RUPAYCC: "/Assets/cards/Rupay-Logo.png",
  };

  const sessionData = {
    SessionID: sessionStorage.getItem("SessionID"),
    Mobilenumber: sessionStorage.getItem("mobileNumber"),
    Name: sessionStorage.getItem("NameOnCard")?.replace(/"/g, ""),
    email: sessionStorage.getItem("Poiemail")?.replace(/"/g, ""),
  };

  const mobileNumber = sessionStorage.getItem("mobileNumber") || "";
  const accountNo = sessionStorage.getItem("accountNo") || "";
  const newOrderId = generateOrderId({ mobileNumber, accountNo });

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));
  const showSnackbar = (message, severity = "info") =>
    setSnackbar({ open: true, message, severity });

  // ─── BIN Lookup ───────────────────────────────────────────────────────────
  const fetchCardInfo = async (bin) => {
    try {
      const res = await api.post(`${baseapi}/tagetpayucardinfo`, {
        ReqType: "TA_Get_PayUCardInfo",
        GeoLocation: locationGeo,
        Platform,
        MachineID: ip,
        EntityID: entity_id,
        Mobilenumber: sessionData.Mobilenumber,
        CardNumber: bin,
        SessionID: sessionData.SessionID,
      });
      if (res.data?.Response_Code === "2980") {
        setCardInfo(res.data.Info);
        setCardType(res.data.Info.cardCategory === "CC" ? "credit" : "debit");
      } else {
        setCardInfo(null);
      }
    } catch (err) {
      console.error("BIN lookup failed", err);
      setCardInfo(null);
    }
  };

  const fetchMerchantKeys = async (cardCategory) => {
    try {
      const res = await api.get(`${baseapi}/getmerchantkeys?cardCategory=${cardCategory}`);
      return res.data;
    } catch (error) {
      console.error("Merchant API failed", error);
      return null;
    }
  };

  // ─── Pay Handler ──────────────────────────────────────────────────────────
  const handlePay = async () => {
    let tempErrors = {};
    if (!amount || Number(amount) < 1)  tempErrors.amount    = "Invalid amount";
    if (!cardNumber || cardNumber.replace(/\s+/g, "").length < 16)
                                        tempErrors.cardNumber = "Enter valid card number";
    if (!expiry || !/^(0[1-9]|1[0-2])\/\d{2}$/.test(expiry))
                                        tempErrors.expiry     = "Enter valid expiry (MM/YY)";
    if (!cvv || cvv.length < 3)         tempErrors.cvv        = "Enter valid CVV";
    if (!nameOnCard.trim())             tempErrors.nameOnCard = "Name on card required";

    setErrors(tempErrors);
    if (Object.keys(tempErrors).length > 0) return;

    setLoading(true);

    try {
      const cardCategory = cardInfo?.cardCategory;
      if (!cardCategory) { showSnackbar("Unable to detect card type", "error"); return; }

      const merchantData = await fetchMerchantKeys(cardCategory);
      if (!merchantData)  { showSnackbar("Failed to fetch merchant details", "error"); return; }

      const { merchant, salt } = merchantData;

      const logResponse = await api.post(`${baseapi}/tapayuinsertlog`, {
        ReqType:     "TA_PayU_PGInsertLogV2",
        Mobilenumber: sessionData.Mobilenumber,
        SessionID:   sessionData.SessionID,
        Amount:      amount,
        HashString:  `${newOrderId}|${amount}|LoadMoney|${sessionData.Name?.toUpperCase()}|${sessionData.email}|||||||||||`,
        Date:        date,
        MachineID:   ip,
        MerchantKey: merchant,
        Salt:        salt,
        Platform,
        EntityID:    entity_id,
        OrderId:     newOrderId,
        GeoLocation: locationGeo,
      });

      const { Response_Code, Message, HashString } = logResponse.data;

      if (Response_Code === "26105") { showSnackbar(Message || "TopUp limit exceeded for today", "error"); return; }
      if (Response_Code !== "2610")  { showSnackbar(Message || "Payment log creation failed!", "error"); return; }

      // ✅ Show navigating overlay immediately before form.submit()
      setIsNavigating(true);

      const form = document.createElement("form");
      form.method = "POST";
      form.action = payuPayment;

      const [expMonth, expYear] = expiry.split("/");

      const params = {
        key:              merchant,
        txnid:            newOrderId,
        amount,
        orderID,
        productinfo:      "LoadMoney",
        firstname:        userData?.FirstName?.toUpperCase(),
        lastname:         userData?.LastName?.toUpperCase(),
        email:            userData?.Poiemail,
        phone:            sessionData.Mobilenumber,
        ccnum:            cardNumber.replace(/\s+/g, ""),
        ccexpmon:         expMonth,
        ccexpyr:          `20${expYear}`,
        ccvv:             cvv,
        ccname:           nameOnCard,
        pg:               cardInfo?.cardCategory,
        bankcode:         "",
        surl:             returnurl,
        furl:             returnurl,
        curl:             returnurl,
        hash:             HashString,
        udf1: "", udf2: "", udf3: "", udf4: "", udf5: "",
        address1: "", address2: "", city: null,
        state: "", country, zipcode: "",
        store_card: "", store_card_token: "", user_credentials: "",
      };
      

      Object.entries(params).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type  = "hidden";
        input.name  = key;
        input.value = value ?? "";
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit(); // navigate immediately — overlay stays visible during browser delay

    } catch (error) {
      console.error("Payment Error:", error);
      showSnackbar("Something went wrong while processing your payment.", "error");
      setIsNavigating(false);
    } finally {
      setLoading(false);
    }
  };

  const handlenavigate = () => navigate("/dashboard/loadmoney");
  const textColor = isDark ? "#ffffff" : "#0e1b2c";

  useEffect(() => {
    const fetchBalanceEnquiry = async () => {
      try {
        const payload = {
          ApplicationType,
          EntityID:    entity_id,
          GeoLocation: locationGeo,
          MachineID:   ip,
          Mobilenumber: sessionStorage.getItem("mobileNumber"),
          Platform,
          ReqType:     "Jan_Aadhaar_eWallet_Balance_Enquiry",
          SessionID:   sessionStorage.getItem("SessionID") || "",
        };
        const response = await api.post(`${baseapi}/JanAadhaareWalletBalanceEnquiry`, payload);
        if (response.data.Response_Code === "1000") {
          setUser((prev) => ({
            ...prev,
            Balance:       response.data.Balance,
            LedgerBalance: response.data.LedgerBalance,
          }));
        }
      } catch (error) {
        console.error("API call error:", error);
      }
    };

    fetchBalanceEnquiry();
    if (location.search.includes("payment=success")) showSnackbar("Amount added successfully", "success");
    if (location.search.includes("payment=failed"))  showSnackbar("Payment Failed", "error");
  }, [location.search]);

  const inputSx = { height: 45, borderRadius: "8px" };

  return (
    <>
      <Box px={7} bgcolor={isDark ? "#081028" : ""}>
        <DashboardHeader />
      </Box>

      <Box sx={{ maxWidth: "1100px", mx: "auto", mt: 4 }}>
        <Box
          sx={{
            borderRadius: "20px",
            overflow: "hidden",
            border: isDark
              ? "1px solid rgba(90, 126, 173, 0.49)"
              : "1px solid rgba(0,0,0,0.2)",
          }}
        >
          <Grid
            container
            display="flex"
            justifyContent="center"
            bgcolor={isDark ? "#0B1C30" : "#f5f5f5"}
            p={5}
          >
            {/* ── LEFT ── */}
            <Grid size={{ sm: 6, md: 6 }} item sm={6} md={6}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3, cursor: "pointer" }}
                onClick={handlenavigate}
              >
                <ArrowBackIosNewOutlinedIcon sx={{ fontSize: 18, color: textColor }} />
                <Typography sx={{ fontSize: "15px", fontWeight: 600, color: textColor }}>Back</Typography>
              </Box>

              <Typography variant="h6" fontWeight={600} mb={1}>
                Wallet Balance : ₹{user?.Balance}
              </Typography>

              <TextField
                fullWidth
                label="Amount"
                variant="standard"
                value={amount}
                InputProps={{
                  readOnly: true,
                  startAdornment: <CurrencyRupeeIcon fontSize="small" sx={{ mr: 1 }} />,
                  sx: { color: isDark ? "#9bb3c9" : "#333" },
                }}
                sx={{ "& input": { cursor: "default", fontWeight: 700, fontSize: 20, pointerEvents: "none" } }}
              />

              <Card
                variant="outlined"
                sx={{ backgroundColor: isDark ? "#12223b" : "#f9f9f9", mb: 3, maxWidth: 500, px: 3, mt: 5 }}
              >
                <CardContent>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>Note:</Typography>
                  <Typography variant="body2">1. Minimum Load Money amount is Rs.10/-.</Typography>
                  <Typography variant="body2">2. Do not use other wallets or virtual cards.</Typography>
                  <Typography variant="body2">
                    3. If amount is debited and wallet is not credited contact customer care
                    or wait for 2 working days before complaining to the bank.
                  </Typography>
                </CardContent>
              </Card>

              <Typography variant="body2" sx={{ color: green[700], mt: 1, cursor: "pointer" }}>
                Know Your Wallet Limits ↓
              </Typography>
            </Grid>

            {/* ── RIGHT ── */}
            <Grid size={{ sm: 6, md: 6 }} item sm={6} md={6}>
              <Box sx={{ maxWidth: 400, mx: "auto" }}>
                <Typography variant="h6" mb={2} fontWeight={600}>Credit/Debit Card</Typography>

                {/* Card Number */}
                <Typography color={textColor} sx={{ mb: 0.5 }}>Card Number</Typography>
                <TextField
                  fullWidth
                  placeholder="1234 5678 9012 3456"
                  value={cardNumber}
                  autoComplete="off"
                  onChange={(e) => {
                    let digits = e.target.value.replace(/\D/g, "").slice(0, 16);
                    if (digits.length >= 6 && !binFetched) { fetchCardInfo(digits.slice(0, 6)); setBinFetched(true); }
                    if (digits.length < 6 && binFetched)   { setBinFetched(false); setCardInfo(null); }
                    setCardNumber(digits.replace(/(.{4})/g, "$1 ").trim());
                  }}
                  error={Boolean(errors.cardNumber)}
                  helperText={errors.cardNumber}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><CreditCardIcon /></InputAdornment>,
                    endAdornment: cardInfo?.cardType && CARD_LOGOS[cardInfo.cardType] ? (
                      <InputAdornment position="end">
                        <img src={CARD_LOGOS[cardInfo.cardType]} alt={cardInfo.cardType}
                          style={{ width: 38, height: "auto", objectFit: "contain" }} />
                      </InputAdornment>
                    ) : null,
                    sx: inputSx,
                  }}
                />

                {/* Expiry + CVV */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ width: "50%" }}>
                    <Typography color={textColor} sx={{ mt: 2 }}>Expiry Date</Typography>
                    <TextField
                      fullWidth placeholder="MM/YY" value={expiry} autoComplete="off"
                      onChange={(e) => {
                        let v = e.target.value.replace(/\D/g, "");
                        if (v.length > 2) v = v.slice(0, 2) + "/" + v.slice(2, 4);
                        setExpiry(v);
                      }}
                      error={Boolean(errors.expiry)} helperText={errors.expiry}
                      InputProps={{ sx: inputSx }}
                    />
                  </Box>
                  <Box sx={{ width: "50%", mt: { md: 1.7 } }}>
                    <Typography color={textColor}>CVV</Typography>
                    <TextField
                      fullWidth placeholder="123" value={cvv} autoComplete="off"
                      onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 3))}
                      error={Boolean(errors.cvv)} helperText={errors.cvv}
                      InputProps={{ sx: inputSx }}
                    />
                  </Box>
                </Box>

                {/* Name on Card */}
                <Typography color={textColor} sx={{ mt: 1, mb: 0.5 }}>Name on Card</Typography>
                <TextField
                  fullWidth placeholder="Enter name as on card" value={nameOnCard} autoComplete="off"
                  onChange={(e) => setNameOnCard(e.target.value.replace(/[^a-zA-Z\s]/g, ""))}
                  error={Boolean(errors.nameOnCard)} helperText={errors.nameOnCard}
                  InputProps={{ sx: inputSx }}
                />

                <Typography variant="body2" color="green" sx={{ display: "flex", alignItems: "center", mb: 2, mt: 1 }}>
                  <LockIcon sx={{ mr: 1 }} /> Secure Payment powered by PayU
                </Typography>

                {/* ── Pay Button ── */}
                <Button
                  variant="contained"
                  fullWidth
                  disabled={loading || isNavigating}
                  onClick={handlePay}
                  sx={{
                    height: 48,
                    fontWeight: "bold",
                    textTransform: "none",
                    fontSize: 15,
                    borderRadius: "10px",
                    bgcolor: isNavigating ? "#166534" : undefined,
                    "&.Mui-disabled": isNavigating ? { bgcolor: "#166534", color: "#fff" } : {},
                    transition: "all 0.3s ease",
                  }}
                >
                  {isNavigating ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />
                      <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>
                        We are navigating, please wait...
                      </Typography>
                    </Box>
                  ) : loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />
                      <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>
                        Processing...
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LockIcon sx={{ fontSize: 18 }} />
                      Verify & Pay ₹{amount}
                    </Box>
                  )}
                </Button>

                {/* ── Full-screen overlay — visible during browser navigation delay ── */}
                {isNavigating && (
                  <Box
                    sx={{
                      position: "fixed",
                      inset: 0,
                      zIndex: 9999,
                      bgcolor: "rgba(0,0,0,0.55)",
                      backdropFilter: "blur(5px)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: isDark ? "#0B1C30" : "#fff",
                        borderRadius: "20px",
                        px: 5,
                        py: 4,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2.5,
                        boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
                        minWidth: 310,
                        maxWidth: 360,
                      }}
                    >
                      {/* Spinner with lock icon */}
                      <Box sx={{ position: "relative", width: 72, height: 72 }}>
                        <CircularProgress
                          size={72} thickness={3}
                          sx={{ color: "#22c55e", position: "absolute", top: 0, left: 0 }}
                        />
                        <Box
                          sx={{
                            position: "absolute", inset: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}
                        >
                          <LockIcon sx={{ fontSize: 30, color: "#22c55e" }} />
                        </Box>
                      </Box>

                      <Typography
                        sx={{ fontWeight: 700, fontSize: 17, color: isDark ? "#fff" : "#0e1b2c", textAlign: "center" }}
                      >
                        We are navigating, please wait...
                      </Typography>

                      <Typography
                        sx={{ fontSize: 13, color: isDark ? "#9bb3c9" : "#6b7280", textAlign: "center", lineHeight: 1.6 }}
                      >
                        You are being redirected to the PayU payment gateway.
                        <br />
                        Please do not press Back or Refresh.
                      </Typography>

                      {/* Sliding progress bar */}
                      <Box
                        sx={{
                          width: "100%", height: 4, borderRadius: 2,
                          bgcolor: isDark ? "#1e3a5f" : "#e5e7eb",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%", width: "40%", bgcolor: "#22c55e",
                            borderRadius: 2,
                            animation: "slide 1.2s ease-in-out infinite",
                            "@keyframes slide": {
                              "0%":   { transform: "translateX(-100%)" },
                              "100%": { transform: "translateX(350%)" },
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%", fontSize: "16px", fontWeight: 500, textAlign: "center" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Payuccdc;